// src/services/policyService.js
//
// Revisión de póliza (paso `policy_review` de config/claimFlow.js).
//
//   1. La póliza (PDF) se respalda como adjunto del reclamo (backup.php).
//   2. "Analizar con IA" → api/policy-analyze.php → Gemini devuelve un
//      BORRADOR. También se puede empezar un borrador a mano.
//   3. Una persona lo revisa y confirma: recién ahí se marca 'reviewed' y los
//      datos principales pasan al reclamo (los usan LOR, SPOL y la ficha).
//
// Todo se guarda en claimRecords/{id}/stepData/policy_review:
//   { status: 'draft' | 'reviewed', source: 'ai' | 'manual', file, draft,
//     data, model, analyzedAt, analyzedBy, reviewedAt, reviewedBy }
// `draft` es lo propuesto (IA o a mano); `data` es lo confirmado.
import { auth, USE_EMULATORS } from '../firebase/config';
import { getStepData, saveStepData } from './flowService';
import { updateClaimRecordFields } from './claimRecordService';
import { uploadBackup } from './backupService';

export const POLICY_STEP_ID = 'policy_review';
const API_URL = process.env.REACT_APP_POLICY_API_URL || '/api/policy-analyze.php';
// Solo en el emulador: servidor local con Gemini de verdad (npm run policy:dev).
// Sin él, el emulador devuelve un análisis simulado.
const DEV_URL = USE_EMULATORS ? process.env.REACT_APP_POLICY_DEV_URL || '' : '';
// En el emulador no hay backup.php: el PDF "subido" queda en memoria de la
// pestaña para poder mandarlo al servidor local.
const emulatorFiles = new Map();

export const CLAUSE_GROUPS = ['exclusions', 'limitations', 'conditions', 'endorsements'];

export const emptyPolicyDraft = () => ({
  insuranceCompanyName: '',
  policyNumber: '',
  policyForm: '',
  namedInsureds: [],
  propertyAddress: '',
  policyEffectiveDate: '',
  policyExpirationDate: '',
  agentAgency: '',
  coverages: [],
  deductibles: [],
  mortgagees: [],
  exclusions: [],
  limitations: [],
  conditions: [],
  endorsements: [],
  warnings: []
});

const asText = value => (typeof value === 'string' ? value.trim() : value == null ? '' : String(value));
const asNumber = value => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(String(value).replace(/[$,\s]/g, ''));
  return Number.isFinite(number) ? number : null;
};
const asPage = value => {
  const number = asNumber(value);
  return number && number > 0 ? Math.round(number) : null;
};
const asList = value => (Array.isArray(value) ? value : []);
const isoDate = value => (/^\d{4}-\d{2}-\d{2}$/.test(asText(value)) ? asText(value) : '');

// Normaliza lo que venga (Gemini, Firestore o el formulario) a la forma fija,
// para que un campo faltante o con otro tipo nunca rompa la pantalla.
export const normalizePolicyDraft = raw => {
  const d = raw || {};
  const clause = item => ({ title: asText(item?.title), summary: asText(item?.summary), page: asPage(item?.page) });
  return {
    insuranceCompanyName: asText(d.insuranceCompanyName),
    policyNumber: asText(d.policyNumber),
    policyForm: asText(d.policyForm),
    namedInsureds: asList(d.namedInsureds).map(asText).filter(Boolean),
    propertyAddress: asText(d.propertyAddress),
    policyEffectiveDate: isoDate(d.policyEffectiveDate),
    policyExpirationDate: isoDate(d.policyExpirationDate),
    agentAgency: asText(d.agentAgency),
    coverages: asList(d.coverages).map(c => ({ code: asText(c?.code).toUpperCase(), name: asText(c?.name), limit: asNumber(c?.limit), page: asPage(c?.page) })),
    deductibles: asList(d.deductibles).map(c => ({ type: asText(c?.type), amount: asNumber(c?.amount), percent: asNumber(c?.percent), page: asPage(c?.page) })),
    mortgagees: asList(d.mortgagees).map(m => ({ name: asText(m?.name), loanNumber: asText(m?.loanNumber) })),
    ...Object.fromEntries(CLAUSE_GROUPS.map(group => [group, asList(d[group]).map(clause)])),
    warnings: asList(d.warnings).map(asText).filter(Boolean)
  };
};

// Borrador inicial "a mano": arranca con lo que el reclamo ya tiene.
export const draftFromClaim = claimRecord => normalizePolicyDraft({
  ...emptyPolicyDraft(),
  insuranceCompanyName: claimRecord.insuranceCompanyName,
  policyNumber: claimRecord.policyNumber,
  policyForm: claimRecord.policyForm,
  policyEffectiveDate: claimRecord.policyEffectiveDate,
  policyExpirationDate: claimRecord.policyExpirationDate,
  agentAgency: claimRecord.agentAgency,
  coverages: ['A', 'B', 'C', 'D']
    .filter(code => claimRecord[`coverage${code}`] !== null && claimRecord[`coverage${code}`] !== undefined && claimRecord[`coverage${code}`] !== '')
    .map(code => ({ code, name: '', limit: claimRecord[`coverage${code}`], page: null })),
  mortgagees: claimRecord.mortgageCompanyName ? [{ name: claimRecord.mortgageCompanyName, loanNumber: claimRecord.mortgageLoanNumber || '' }] : []
});

// Campos del reclamo que llena una revisión confirmada.
export const POLICY_CLAIM_KEYS = [
  'insuranceCompanyName', 'policyNumber', 'policyForm', 'policyEffectiveDate', 'policyExpirationDate', 'agentAgency',
  'coverageA', 'coverageB', 'coverageC', 'coverageD', 'deductibleAmount', 'deductibleType',
  'mortgageCompanyName', 'mortgageLoanNumber'
];

// Solo pasa al reclamo lo que la revisión trae con valor: un dato vacío en
// la póliza no borra lo que el equipo ya había cargado.
export const policyToClaimFields = data => {
  const d = normalizePolicyDraft(data);
  const changes = {};
  // policyForm (HO-3, DP-3…) decide Owner/Tenant en la POL (spolData.js).
  ['insuranceCompanyName', 'policyNumber', 'policyForm', 'policyEffectiveDate', 'policyExpirationDate', 'agentAgency'].forEach(key => {
    if (d[key]) changes[key] = d[key];
  });
  ['A', 'B', 'C', 'D'].forEach(code => {
    const coverage = d.coverages.find(c => c.code === code && c.limit !== null);
    if (coverage) changes[`coverage${code}`] = coverage.limit;
  });
  // El deducible del reclamo es el general (All Other Perils); si no está
  // identificado, el primero que tenga valor.
  const deductible = d.deductibles.find(x => /all other|aop/i.test(x.type) && (x.amount !== null || x.percent !== null))
    || d.deductibles.find(x => x.amount !== null || x.percent !== null);
  if (deductible) {
    if (deductible.amount !== null) {
      changes.deductibleAmount = deductible.amount;
      changes.deductibleType = 'fixed';
    } else {
      changes.deductibleAmount = deductible.percent;
      changes.deductibleType = 'percentage';
    }
  }
  const mortgagee = d.mortgagees.find(m => m.name);
  if (mortgagee) {
    changes.mortgageCompanyName = mortgagee.name;
    if (mortgagee.loanNumber) changes.mortgageLoanNumber = mortgagee.loanNumber;
  }
  return changes;
};

export const getPolicyReview = claimRecordId => getStepData('claim', claimRecordId, POLICY_STEP_ID);

// Respaldar el PDF como adjunto del reclamo. En el emulador no hay PHP: se
// simula la clave para poder probar la pantalla completa.
export const uploadPolicyFile = async (claimRecordId, file) => {
  if (!file || file.type !== 'application/pdf') throw new Error('La póliza debe ser un PDF.');
  const stored = USE_EMULATORS
    ? { storageKey: `${claimRecordId}/attachment/${'0'.repeat(32)}.pdf`, name: file.name, size: file.size, uploadedAt: new Date().toISOString() }
    : await uploadBackup({ file, claimId: claimRecordId, invoiceNumber: '', type: 'attachment' });
  if (USE_EMULATORS) emulatorFiles.set(stored.storageKey, file);
  const fileRef = { storageKey: stored.storageKey, name: stored.name || file.name, size: stored.size || file.size, uploadedAt: stored.uploadedAt || new Date().toISOString() };
  await saveStepData('claim', claimRecordId, POLICY_STEP_ID, { file: fileRef });
  return fileRef;
};

// Quitar la póliza subida por error: solo se desvincula del reclamo (el respaldo
// en el servidor no se borra); el borrador y lo ya confirmado no se tocan.
export const removePolicyFile = claimRecordId =>
  saveStepData('claim', claimRecordId, POLICY_STEP_ID, { file: null });

const simulatedAnalysis = () => new Promise(resolve => setTimeout(() => resolve({
  simulated: true,
  model: 'simulado (emulador)',
  analyzedAt: new Date().toISOString(),
  draft: {
    ...emptyPolicyDraft(),
    insuranceCompanyName: 'Ejemplo Insurance Company',
    policyNumber: 'HO3-000000',
    policyForm: 'HO-3',
    namedInsureds: ['Asegurado de Prueba'],
    policyEffectiveDate: '2026-01-01',
    policyExpirationDate: '2027-01-01',
    coverages: [
      { code: 'A', name: 'Dwelling', limit: 350000, page: 1 },
      { code: 'B', name: 'Other Structures', limit: 7000, page: 1 },
      { code: 'C', name: 'Personal Property', limit: 175000, page: 1 },
      { code: 'D', name: 'Loss of Use', limit: 35000, page: 1 }
    ],
    deductibles: [
      { type: 'All Other Perils', amount: 2500, percent: null, page: 1 },
      { type: 'Hurricane', amount: null, percent: 2, page: 1 }
    ],
    exclusions: [{ title: 'Constant or Repeated Seepage', summary: 'Resultado simulado: excluye filtraciones continuas por 14 días o más.', page: 12 }],
    warnings: ['Resultado SIMULADO del emulador — no viene de Gemini.']
  }
}), 800));

const analyzeWithDevServer = async storageKey => {
  const file = emulatorFiles.get(storageKey);
  if (!file) throw new Error('En el emulador el PDF solo vive en esta pestaña: vuelve a subir la póliza y analiza.');
  const body = new FormData();
  body.append('file', file);
  let response;
  try {
    response = await fetch(`${DEV_URL}/analyze`, { method: 'POST', body });
  } catch (error) {
    throw new Error('No responde el servidor local de Gemini: corre "npm run policy:dev".');
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'No se pudo analizar la póliza.');
  return payload;
};

export const analyzePolicy = async storageKey => {
  if (USE_EMULATORS) return DEV_URL ? analyzeWithDevServer(storageKey) : simulatedAnalysis();
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión.');
  const token = await user.getIdToken();
  const response = await fetch(API_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ storageKey })
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'No se pudo analizar la póliza.');
  return payload;
};

// ── Respaldo manual: usar cualquier otra IA ─────────────────────────────
// Cuando Gemini está saturado: se copian las MISMAS instrucciones que usa el
// servidor (public/api/policy-prompt.txt) más el formato de respuesta, se
// pegan en ChatGPT/Claude/Gemini con el PDF, y la respuesta se pega de vuelta.
const clause = { title: 'Nombre de la cláusula (idioma original)', summary: 'Qué dice y cómo afecta al reclamo, en español', page: 1 };
const ANSWER_SHAPE = {
  insuranceCompanyName: '', policyNumber: '', policyForm: 'HO-3', namedInsureds: [''], propertyAddress: '',
  policyEffectiveDate: 'YYYY-MM-DD', policyExpirationDate: 'YYYY-MM-DD', agentAgency: '',
  coverages: [{ code: 'A', name: 'Dwelling', limit: 0, page: 1 }],
  deductibles: [{ type: 'All Other Perils', amount: 0, percent: null, page: 1 }],
  mortgagees: [{ name: '', loanNumber: '' }],
  exclusions: [clause], limitations: [clause], conditions: [clause], endorsements: [clause],
  warnings: ['Dato que no se encontró o es ambiguo']
};

export const buildManualPolicyPrompt = async () => {
  // En producción el texto se pide a policy-prompt.php con la sesión (el .txt
  // está bloqueado por URL); en local/emulador se sirve el archivo directo.
  const override = process.env.REACT_APP_POLICY_PROMPT_URL;
  const user = auth.currentUser;
  const response = !override && !USE_EMULATORS && user
    ? await fetch('/api/policy-prompt.php', { headers: { Authorization: `Bearer ${await user.getIdToken()}` } })
    : await fetch(override || '/api/policy-prompt.txt');
  if (!response.ok) throw new Error('No se pudieron cargar las instrucciones.');
  const instructions = (await response.text()).trim();
  return [
    instructions,
    '',
    'Formato de la respuesta: devuelve SOLO un objeto JSON válido, sin texto antes ni después y sin comentarios, con exactamente estas claves (las listas pueden tener todos los elementos que haga falta):',
    JSON.stringify(ANSWER_SHAPE, null, 2)
  ].join('\n');
};

// Lee la respuesta pegada: tolera texto alrededor y bloques ```json.
export const parsePolicyAnswer = text => {
  const raw = String(text || '');
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) throw new Error('No encontré el JSON en la respuesta. Copia la respuesta completa de la IA.');
  let parsed;
  try {
    parsed = JSON.parse(raw.slice(start, end + 1));
  } catch (error) {
    throw new Error('La respuesta no es un JSON válido (quizás se cortó). Pídele a la IA que la repita completa y en un solo bloque.');
  }
  const known = Object.keys(ANSWER_SHAPE).filter(key => key in parsed);
  if (known.length < 3) throw new Error('La respuesta no tiene el formato de la póliza. Revisa que la IA haya usado las instrucciones copiadas.');
  return normalizePolicyDraft(parsed);
};

export const savePolicyDraft = async (claimRecordId, draft, meta = {}) => {
  const changes = { status: 'draft', draft: normalizePolicyDraft(draft), ...meta };
  await saveStepData('claim', claimRecordId, POLICY_STEP_ID, changes);
  return changes;
};

// Confirmación humana: guarda lo revisado y pasa los datos al reclamo.
// Devuelve { review, claimChanges } para refrescar la ficha.
export const confirmPolicyReview = async (claimRecord, data) => {
  const reviewed = normalizePolicyDraft(data);
  const review = {
    status: 'reviewed',
    draft: reviewed,
    data: reviewed,
    reviewedAt: new Date().toISOString(),
    reviewedBy: auth.currentUser?.email || ''
  };
  // Primero los datos del reclamo y después la marca de "revisada": si falla la
  // segunda, la revisión sigue como borrador y se puede volver a confirmar
  // (nunca queda "Revisada ✓" sin haber copiado los datos).
  const claimChanges = await updateClaimRecordFields(claimRecord.id, policyToClaimFields(reviewed), POLICY_CLAIM_KEYS);
  await saveStepData('claim', claimRecord.id, POLICY_STEP_ID, review);
  return { review, claimChanges };
};
